import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { twMerge } from "@/utils/twMerge";

import { useTreeItemKeys } from "../useSidebarNav";

import type { IconName } from "@/components/icons/Icon";
import type { ElementType } from "react";

type Props<T extends ElementType> = {
  title: React.ReactNode;
  iconName?: IconName;
  className?: string;
  as?: T;
} & Omit<React.ComponentPropsWithoutRef<T>, "title" | "className">;

export function ListItemButton<T extends ElementType = "button">({
  title,
  iconName,
  className,
  as,
  ...rest
}: Props<T>) {
  const Component = as || "button";

  return (
    <Component
      className={twMerge(
        "pl-[22px] pr-5 relative group rounded-6 flex items-center w-full min-h-7.5 text-gray-500",
        "hover:bg-sidebar-button-hover-inverted-bg hover:text-document-text",
        className,
      )}
      {...rest}
    >
      {iconName && <Icon name={iconName} />}
      <div
        className={twMerge(
          "truncate",
          "h-full ml-0.5 py-[5px]",
          "font-geist text-[16px] leading-[20px] tracking-tight text-left",
          "sm:ml-1 sm:py-[9px] sm:text-[17px] sm:leading-[20px]",
        )}
      >
        {title}
      </div>
    </Component>
  );
}

export function ShowMoreButton({
  label,
  onClick,
}: {
  /** Section-specific name starting with the visible "More", e.g. "More documents" */
  label: string;
  onClick: () => void;
}) {
  const t = useTranslations("documentsSidebar");
  const onKeyDown = useTreeItemKeys({});

  return (
    <ListItemButton
      type="button"
      role="treeitem"
      onKeyDown={onKeyDown}
      title={t("showMore")}
      aria-label={label}
      onClick={onClick}
      className="text-blue-500 hover:text-blue-500"
    />
  );
}
